import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Wallet, Save, Loader2, Lock, PackageOpen, Flame, Lock as LockIcon, Cpu } from "lucide-react";

const SEZ_ICON = { incendio: Flame, furto: LockIcon, elettronica: Cpu };

export default function MultirischiBeniQuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const editId = params.get("edit");
  const { isAdmin } = useAuth();
  const [editLoaded, setEditLoaded] = useState(false);

  const [provincia, setProvincia] = useState("");
  const [comune, setComune] = useState("");
  const [cap, setCap] = useState("");
  const [indirizzoRischio, setIndirizzoRischio] = useState("");
  const [ubicazioneCoincide, setUbicazioneCoincide] = useState(true);
  const [attivita, setAttivita] = useState("");
  const [sezioni, setSezioni] = useState({ incendio: true, furto: false, elettronica: false });
  const [partite, setPartite] = useState({});
  const [refrigClass, setRefrigClass] = useState(1);
  const [allarme, setAllarme] = useState("nessuno");
  const [slider, setSlider] = useState(0);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/multirischi/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
  }, []);
  useEffect(() => {
    if (!anagraficaId) return;
    const a = anagrafiche.find((x) => x.id === anagraficaId);
    if (a && ubicazioneCoincide) {
      setProvincia((a.provincia || "").toUpperCase()); setComune(a.comune || ""); setCap(a.cap || "");
      setIndirizzoRischio(a.indirizzo || "");
    }
  }, [anagraficaId, anagrafiche, ubicazioneCoincide]);

  const attMeta = useMemo(() => (config?.attivita || []).find((a) => a.nome === attivita), [config, attivita]);
  const setP = (k, v) => setPartite((p) => ({ ...p, [k]: v }));

  // Modalità modifica: carica il preventivo esistente
  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.mb_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      if (i.provincia) setProvincia(i.provincia);
      if (i.comune) setComune(i.comune);
      if (i.cap) setCap(i.cap);
      if (i.indirizzo_rischio != null) setIndirizzoRischio(i.indirizzo_rischio);
      if (i.ubicazione_coincide != null) setUbicazioneCoincide(i.ubicazione_coincide);
      setAttivita(i.attivita || "");
      if (i.sezioni) setSezioni(i.sezioni);
      if (i.partite) setPartite(i.partite);
      if (i.refrig_class != null) setRefrigClass(i.refrig_class);
      if (i.furto_allarme) setAllarme(i.furto_allarme);
      setSlider(i.slider_pct || 0);
      if (i.data_effetto) setDataEffetto(i.data_effetto);
      if (q.sinistri) setSinistri(q.sinistri);
      if (q.idd) setIdd(q.idd);
      setEditLoaded(true);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded]);

  const buildPayload = () => ({
    provincia: provincia.trim().toUpperCase(), comune, cap,
    indirizzo_rischio: indirizzoRischio, ubicazione_coincide: ubicazioneCoincide,
    attivita,
    sezioni,
    partite: Object.fromEntries(Object.entries(partite).map(([k, v]) => [k, Number(v || 0)])),
    refrig_class: Number(refrigClass), furto_allarme: allarme, slider_pct: slider, data_effetto: dataEffetto, sinistri, idd,
  });

  const calculate = async () => {
    setCalculating(true);
    try { const { data } = await api.post("/multirischi/quotes/calculate", buildPayload()); setResult(data); }
    catch (e) { toast.error(apiError(e)); } finally { setCalculating(false); }
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/multirischi/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Preventivo non calcolabile", msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico richiesto",
        msgs: [...(result.eligibility.warnings || []), ...(result.authority_exceeded ? [`Flessibilità oltre la tua autonomia (max +${result.slider_max_autonomo}%).`] : [])] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo standard — emettibile", msgs: result.eligibility.warnings || [] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  const q = [anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&");
  const qs = q ? `?${q}` : "";
  const premium = result?.premium;
  const hasRefrig = Number(partite.incendio_merci_refrig_celle || 0) > 0;

  return (
    <div className="space-y-6">
      <div>
        <div className={`mb-3 flex flex-wrap gap-2 ${editId ? "hidden" : ""}`} data-testid="product-switch">
          <Link to={`/quota/nuovo${qs}`} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">RC Economia Solidale</Link>
          <Link to={`/quota/mrv${qs}`} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Multirischi Volontariato</Link>
          <Link to={`/quota/infortuni${qs}`} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Infortuni</Link>
          <Link to={`/quota/catnat${qs}`} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">CAT NAT</Link>
          <span className="flex items-center gap-1.5 rounded-full border border-forest bg-forest px-4 py-1.5 text-sm font-medium text-white"><PackageOpen className="h-4 w-4" /> Multirischi Danni ai Beni</span>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Preventivo — Tutela Multirischi Danni ai Beni</h1>
        <p className="mt-1 text-muted-foreground">Ed. {config.product_version} · Enti del Terzo Settore e Botteghe del Mondo · premio minimo {eur(config.min_premio_annuo_lordo)} lordo.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Anagrafica e rischio</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Anagrafica cliente</Label>
                <Select value={anagraficaId} onValueChange={setAnagraficaId}>
                  <SelectTrigger data-testid="mb-anagrafica"><SelectValue placeholder="Seleziona ente..." /></SelectTrigger>
                  <SelectContent>{anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border border-border/60 bg-sage/30 p-3">
                <label className="flex cursor-pointer items-center justify-between">
                  <span className="text-sm font-medium text-forest">Ubicazione del rischio = sede legale del contraente</span>
                  <Switch data-testid="mb-ubicazione-coincide" checked={ubicazioneCoincide}
                    onCheckedChange={(v) => setUbicazioneCoincide(!!v)} />
                </label>
                <p className="mt-1 text-xs text-muted-foreground">
                  {ubicazioneCoincide
                    ? "Il fabbricato/i beni assicurati si trovano all'indirizzo della sede legale."
                    : "Indica l'indirizzo dove si trovano effettivamente i beni assicurati (diverso dalla sede legale)."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Indirizzo ubicazione del rischio (via e n. civico)</Label>
                <Input data-testid="mb-indirizzo-rischio" value={indirizzoRischio} disabled={ubicazioneCoincide}
                  onChange={(e) => setIndirizzoRischio(e.target.value)} placeholder="es. Via Roma 10" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label>Comune</Label><Input data-testid="mb-comune" value={comune} disabled={ubicazioneCoincide} onChange={(e) => setComune(e.target.value)} placeholder="es. Milano" /></div>
                <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid="mb-provincia" value={provincia} maxLength={2} disabled={ubicazioneCoincide} onChange={(e) => setProvincia(e.target.value.toUpperCase())} placeholder="MI" /></div>
                <div className="space-y-1.5"><Label>CAP</Label><Input data-testid="mb-cap" value={cap} disabled={ubicazioneCoincide} onChange={(e) => setCap(e.target.value)} placeholder="20100" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Attività esercitata</Label>
                <Input data-testid="mb-attivita" list="mb-attivita-list" value={attivita} onChange={(e) => setAttivita(e.target.value)} placeholder="Digita per cercare (es. Uffici, Ristoranti, Farmacia...)" />
                <datalist id="mb-attivita-list">
                  {config.attivita.map((a) => <option key={a.nome} value={a.nome}>{a.settore}</option>)}
                </datalist>
                {attMeta && (
                  <div data-testid="mb-classi-badge" className="mt-1 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-sage px-2.5 py-0.5 text-forest">Settore: {attMeta.settore}</span>
                    <span className={`rounded-full px-2.5 py-0.5 ${[5, 6].includes(attMeta.classe_incendio) ? "bg-amber-100 text-amber-800" : "bg-forest/10 text-forest"}`}>Classe Incendio {attMeta.classe_incendio}{[5, 6].includes(attMeta.classe_incendio) ? " (RD)" : ""}</span>
                    <span className={`rounded-full px-2.5 py-0.5 ${attMeta.classe_furto === "RD" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}`}>Classe Furto {attMeta.classe_furto}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Sezioni */}
          {config.sezioni.map((s) => {
            const Icon = SEZ_ICON[s.code] || PackageOpen;
            return (
              <Card key={s.code} className="rounded-xl border-border/60 p-5">
                <label className="flex cursor-pointer items-center justify-between">
                  <span className="flex items-center gap-2 font-heading text-lg font-semibold text-forest"><Icon className="h-5 w-5 text-terracotta" /> {s.label}</span>
                  <Switch data-testid={`mb-sezione-${s.code}`} checked={!!sezioni[s.code]} onCheckedChange={(v) => setSezioni({ ...sezioni, [s.code]: !!v })} />
                </label>

                {sezioni.incendio && s.code === "incendio" && (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {config.incendio_partite.map((p) => (
                      <div key={p.code} className="space-y-1">
                        <Label className="text-xs">{p.label}</Label>
                        <Input data-testid={`mb-incendio-${p.code}`} type="number" min={0} value={partite[`incendio_${p.code}`] || ""} onChange={(e) => setP(`incendio_${p.code}`, e.target.value)} placeholder="€ 0" />
                      </div>
                    ))}
                    {hasRefrig && (
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Classe merci in refrigerazione</Label>
                        <Select value={String(refrigClass)} onValueChange={(v) => setRefrigClass(Number(v))}>
                          <SelectTrigger data-testid="mb-refrig-class"><SelectValue /></SelectTrigger>
                          <SelectContent>{config.refrig_classi.map((c) => <SelectItem key={c.code} value={String(c.code)}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {sezioni.furto && s.code === "furto" && (
                  <div className="mt-4 space-y-3">
                    <div className="space-y-1"><Label className="text-xs">Macchinario, attrezzature, arredamento, merci (Primo Rischio)</Label>
                      <Input data-testid="mb-furto-macchinario" type="number" min={0} value={partite.furto_macchinario_merci || ""} onChange={(e) => setP("furto_macchinario_merci", e.target.value)} placeholder="€ 0 (max 100.000)" /></div>
                    <div className="space-y-1"><Label className="text-xs">Furto merci in aree all'aperto (+20%)</Label>
                      <Input data-testid="mb-furto-aperto" type="number" min={0} value={partite.furto_merci_aperto || ""} onChange={(e) => setP("furto_merci_aperto", e.target.value)} placeholder="€ 0 (max 25% del precedente)" /></div>
                    <div className="space-y-1"><Label className="text-xs">Impianto di allarme</Label>
                      <Select value={allarme} onValueChange={setAllarme}>
                        <SelectTrigger data-testid="mb-furto-allarme"><SelectValue /></SelectTrigger>
                        <SelectContent>{config.furto_allarmi.map((al) => <SelectItem key={al.code} value={al.code}>{al.label}</SelectItem>)}</SelectContent>
                      </Select></div>
                  </div>
                )}

                {sezioni.elettronica && s.code === "elettronica" && (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {config.elettronica_partite.map((p) => (
                      <div key={p.code} className="space-y-1">
                        <Label className="text-xs">{p.label}</Label>
                        <Input data-testid={`mb-elettronica-${p.code}`} type="number" min={0} value={partite[`elettronica_${p.code}`] || ""} onChange={(e) => setP(`elettronica_${p.code}`, e.target.value)} placeholder="€ 0" />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Dichiarazione sinistri</h2>
            <SinistriDeclaration value={sinistri} onChange={setSinistri}
              periodoAnni={SINISTRI_YEARS.multirischi_beni} testidPrefix="mb-sinistri" />
              <div className="mt-4 border-t border-border/60 pt-4">
                <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                <IddDeclaration value={idd} onChange={setIdd} testidPrefix="mb-idd" />
              </div>
          </Card>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Flessibilità commerciale</h2>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{slider < 0 ? "Sconto" : "Aumento"}</span>
              <span data-testid="mb-slider-value" className={`font-heading text-2xl font-semibold ${slider < 0 ? "text-terracotta" : "text-forest"}`}>{slider > 0 ? "+" : ""}{slider}%</span>
            </div>
            <Slider data-testid="mb-slider" className="mt-3" value={[slider]} min={-config.flessibilita_max} max={config.flessibilita_max} step={1} onValueChange={(v) => setSlider(v[0])} />
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>-{config.flessibilita_max}%</span><span>+{config.flessibilita_max}%</span></div>

            <div className="mt-4 space-y-1.5"><Label>Data di effetto</Label>
              <Input data-testid="mb-data-effetto" type="date" value={dataEffetto} onChange={(e) => setDataEffetto(e.target.value)} /></div>

            <Button data-testid="mb-calculate-button" onClick={calculate} disabled={calculating || !attivita}
              className="mt-5 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {calculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Calculator className="h-5 w-5" /> Calcola premio</>}
            </Button>

            {result && (
              <div className="mt-5 space-y-4" data-testid="mb-quote-result">
                {statusBanner && (
                  <div className={`flex gap-2 rounded-lg border p-3 text-sm ${statusBanner.tone}`}>
                    <statusBanner.icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div><div className="font-medium">{statusBanner.title}</div>
                      {statusBanner.msgs.map((m, i) => <div key={i} className="mt-0.5 text-xs opacity-90">{m}</div>)}</div>
                  </div>
                )}
                {premium && result.sezioni && (
                  <div className="rounded-lg bg-sage/50 p-4">
                    {Object.entries(result.sezioni).map(([code, sez]) => (
                      <div key={code} className="mb-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-forest/70">{code}</div>
                        {sez.rows.map((r, i) => (
                          <div key={i} className="flex justify-between text-xs"><span className="text-muted-foreground">{r.label}</span><span className="text-forest">{eur(r.net)}</span></div>
                        ))}
                        <div className="flex justify-between text-xs font-medium text-forest"><span>Lordo sezione</span><span>{eur(sez.gross_total)}</span></div>
                      </div>
                    ))}
                    <div className="mt-1 flex items-baseline justify-between border-t border-border pt-2">
                      <span className="font-medium text-forest">Premio annuo lordo</span>
                      <span data-testid="mb-premio-finale" className="font-heading text-2xl font-semibold text-forest">{eur(premium.gross_total)}</span>
                    </div>
                    {premium.minimum_premium_applied && <div className="mt-1 text-xs text-amber-700">Applicato premio minimo di polizza (€ 150,00 lordo).</div>}
                  </div>
                )}
                {result.provvigione && (
                  <div data-testid="mb-commission-box" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-terracotta"><Wallet className="h-3.5 w-3.5" /> Provvigione (solo per te)</div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{result.provvigione.pct}% dell'imponibile</span>
                      <span className="font-heading text-xl font-semibold text-terracotta">{eur(result.provvigione.importo)}</span>
                    </div>
                  </div>
                )}
                {result.status !== "non_ammissibile" && (
                  <Button data-testid="mb-save-quote-button" onClick={save} disabled={saving || !anagraficaId}
                    className="h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" /> Salva preventivo</>}
                  </Button>
                )}
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Le provvigioni non compaiono mai nella vista cliente.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
